import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, Lock, User, LogIn } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const pageTitle = isLogin ? 'Sign In – ScoreCite' : 'Create Account – ScoreCite';

  // Check for pending report to claim after login
  const claimPendingReport = async () => {
    const pendingAuditId = localStorage.getItem('pendingClaimAuditId');
    if (pendingAuditId) {
      try {
        const res = await axios.post(`${API}/audit/${pendingAuditId}/claim`, {}, { withCredentials: true });
        localStorage.removeItem('pendingClaimAuditId');
        toast.success('Report saved to your dashboard!');
        return res.data.new_audit_id;
      } catch (err) {
        // Silently fail if already claimed or other issue
        localStorage.removeItem('pendingClaimAuditId');
        if (err.response?.data?.detail?.includes('already have')) {
          toast.info('This report is already in your dashboard');
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const user = await login(email, password);
        toast.success(`Welcome back, ${user.name || user.email}!`);
      } else {
        const user = await register(email, password, name, marketingConsent);
        toast.success(`Account created! Welcome, ${user.name}!`);
      }
      
      // Check if there's a report to claim
      const newAuditId = await claimPendingReport();
      
      // Navigate to the claimed report or dashboard
      if (newAuditId) {
        navigate(`/report/${newAuditId}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <Helmet>
        <title>Sign In – ScoreCite</title>
        <meta name="description" content="Sign in or create an account to start auditing your website for SEO and AI readiness with ScoreCite." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/">
            <img src="/scorecite-logo.png" alt="ScoreCite" className="h-20 mx-auto cursor-pointer hover:opacity-90 transition-opacity" />
          </Link>
        </div>

        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-center">
              {isLogin ? 'Sign In' : 'Create Account'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label className="text-zinc-400">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      data-testid="register-name-input"
                      className="pl-10 bg-[#0D0D0D] border-white/10 text-white"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-zinc-400">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="auth-email-input"
                    className="pl-10 bg-[#0D0D0D] border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="auth-password-input"
                    className="pl-10 bg-[#0D0D0D] border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Marketing Consent Checkbox - only for registration */}
              {!isLogin && (
                <div className="flex items-start gap-3 py-2">
                  <Checkbox
                    id="marketing-consent"
                    checked={marketingConsent}
                    onCheckedChange={setMarketingConsent}
                    className="mt-1 border-white/20 data-[state=checked]:bg-blue-600"
                    data-testid="marketing-consent-checkbox"
                  />
                  <Label htmlFor="marketing-consent" className="text-zinc-400 text-sm leading-relaxed cursor-pointer">
                    I agree to receive product updates, tips and promotional emails from ScoreCite. 
                    You can unsubscribe at any time. See our <Link to="/terms" className="text-blue-400 hover:underline">Privacy Policy</Link>.
                  </Label>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-button"
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LogIn className="w-4 h-4 mr-2" />
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </Button>

              {isLogin && (
                <div className="text-center mt-3">
                  <Link to="/reset-password" className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors">
                    Forgot password?
                  </Link>
                </div>
              )}
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                data-testid="auth-toggle-mode"
                className="text-sm text-zinc-400 hover:text-white transition-colors"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
